import React, { forwardRef, useEffect, useState } from 'react';
import { DatePicker as RsDatePicker } from 'rsuite';

// rsuite's DatePicker, made safe to type a date into. Same props, same Date in and out, so it
// drops in wherever rsuite's is used (including as a Form.Control accepter).
//
// While a year is typed the picker reports the partial years on the way (0002, 0020, 0202).
// Feeding those back in as the value reset the field under the cursor, and new Date(2, ...)
// reads years under 100 as 19xx, so "12202026" ended up as some date in 1902. Here the picker
// keeps its own draft while typing and the page only hears complete dates (or a clear).

// Earliest year a typed date may have; anything earlier is a year still being typed.
export const MIN_YEAR = 1900;

const isDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());
const isUsableDate = (d) => isDate(d) && d.getFullYear() >= MIN_YEAR;

const pad = (n, width = 2) => String(n).padStart(width, '0');

// Two dates match if they agree as far as the format shows (the page may drop the time).
const dateKey = (d, withTime) => {
  if (!isDate(d)) return '';
  const day = `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${day}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}` : day;
};

// Dates read MM/dd/yyyy across the app unless a picker asks for something else (MM/yyyy).
const DatePicker = forwardRef(function DatePicker({ value, onChange, format = 'MM/dd/yyyy', ...props }, ref) {
  const withTime = /[Hhms]/.test(format || '');
  const [draft, setDraft] = useState(() => (isDate(value) ? value : null));

  // A new value from outside (saved, cleared, another field) replaces the draft, unless it is the
  // one the draft already shows.
  useEffect(() => {
    setDraft((current) => (dateKey(current, withTime) === dateKey(value, withTime) ? current : isDate(value) ? value : null));
  }, [value, withTime]);

  const handleChange = (next, event) => {
    setDraft(next);
    if (next === null || next === undefined) onChange?.(null, event);
    else if (isUsableDate(next)) onChange?.(next, event);
    // Otherwise a year is part-way typed: keep the draft and wait for the rest.
  };

  return <RsDatePicker ref={ref} {...props} format={format} value={draft} onChange={handleChange} />;
});

export default DatePicker;
